import { useEffect, useRef, useState } from 'react';
import { Input, Spin, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { posService } from '@/services/pos.service';
import type { PosCustomerSearchItem } from '@/pages/POS/types';

const { Text } = Typography;
const MIN_LEN = 3;
const DEBOUNCE_MS = 400;

interface Props {
  onSelect: (item: PosCustomerSearchItem) => void;
  /**
   * Khi có label, input dùng pattern floating-label (CSS chung của
   * CustomerSidebar). Khi không có, render plain Input.
   */
  label?: string;
}

function highlight(text: string | undefined | null, query: string) {
  if (!text) return null;
  const trimmed = query.trim();
  if (!trimmed) return text;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const splitRegex = new RegExp(`(${escaped})`, 'gi');
  const matchRegex = new RegExp(`^${escaped}$`, 'i');
  const parts = String(text).split(splitRegex);
  return parts.map((part, i) =>
    part && matchRegex.test(part) ? (
      <span
        key={i}
        style={{ backgroundColor: '#FF6B00', color: '#fff', padding: '0 2px', borderRadius: 2 }}
      >
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function joinAddress(addr: PosCustomerSearchItem['address']) {
  return [addr.address, addr.ward?.name, addr.city?.name].filter(Boolean).join(', ');
}

export function CustomerSearchField({ onSelect, label }: Props) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<PosCustomerSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [focused, setFocused] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextRef = useRef(false);
  const lastTypedRef = useRef('');
  const blurCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (skipNextRef.current) {
      skipNextRef.current = false;
      setItems([]);
      setLoading(false);
      return;
    }
    const trimmed = query.trim();
    if (trimmed.length < MIN_LEN) {
      setItems([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setHasSearched(false);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await posService.searchCustomers(trimmed);
        if (lastTypedRef.current !== query) return;
        setItems(res.data ?? []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
        setHasSearched(true);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => () => {
    if (blurCloseRef.current) clearTimeout(blurCloseRef.current);
  }, []);

  const popupOpen = loading || items.length > 0 || hasSearched;

  function handleSelect(item: PosCustomerSearchItem) {
    onSelect(item);
    skipNextRef.current = true;
    if (blurCloseRef.current) clearTimeout(blurCloseRef.current);
    setItems([]);
    setLoading(false);
    setHasSearched(false);
    setQuery('');
    lastTypedRef.current = '';
  }

  function handleBlur() {
    setFocused(false);
    if (blurCloseRef.current) clearTimeout(blurCloseRef.current);
    blurCloseRef.current = setTimeout(() => {
      setItems([]);
      setLoading(false);
      setHasSearched(false);
    }, 500);
  }

  function handleFocus() {
    setFocused(true);
    if (blurCloseRef.current) {
      clearTimeout(blurCloseRef.current);
      blurCloseRef.current = null;
    }
  }

  const isFloating = focused || query.length > 0;
  // Khi dùng floating label, áp dụng CSS chung của CustomerSidebar
  // (.floating-field) để input cao 36px + label float lên khi focus/has value.
  const wrapperClass = label
    ? ['floating-field', 'floating-field--search', isFloating ? 'is-floating' : '']
        .filter(Boolean)
        .join(' ')
    : '';

  return (
    <div className={wrapperClass} style={{ position: 'relative' }}>
      <Input
        prefix={<SearchOutlined style={{ color: '#999' }} />}
        placeholder={label ? 'Nhập SĐT, tên, MST...' : 'Nhập SĐT, tên, MST...'}
        value={query}
        onChange={(e) => {
          lastTypedRef.current = e.target.value;
          setQuery(e.target.value);
        }}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
      {label && <span className="floating-field-label">{label}</span>}

      {popupOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
            zIndex: 1400,
            maxHeight: 480,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div style={{ padding: 12, textAlign: 'center' }}>
              <Spin size="small" />
            </div>
          )}

          {!loading && items.length === 0 && hasSearched && (
            <div style={{ padding: '8px 12px' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Không tìm thấy khách hàng phù hợp
              </Text>
            </div>
          )}

          {!loading &&
            items.map((item) => {
              const u = item.user;
              const a = item.address;
              return (
                <div
                  key={`${u._id}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                  style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong>
                      {highlight(u.phone, query)} - {highlight(u.name, query)}
                    </strong>
                    {u.user_type && (
                      <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 10 }}>
                        {u.user_type === 'personal' ? 'Cá nhân' : 'Tổ chức'}
                      </Tag>
                    )}
                    {u.group?.name && (
                      <Tag color="purple" style={{ marginInlineEnd: 0, fontSize: 10 }}>
                        {u.group.name}
                      </Tag>
                    )}
                  </div>
                  <div style={{ marginTop: 4, color: '#666' }}>
                    {u.misa_account_object_code && (
                      <div>
                        Mã KH: <strong>{highlight(u.misa_account_object_code, query)}</strong>
                      </div>
                    )}
                    {a.email && (
                      <div>
                        Email: <strong>{highlight(a.email, query)}</strong>
                      </div>
                    )}
                    {u.user_type === 'personal' && a.citizen_id && (
                      <div>
                        CCCD: <strong>{highlight(a.citizen_id, query)}</strong>
                      </div>
                    )}
                    {u.user_type === 'organization' && u.tax_code && (
                      <div>
                        MST: <strong>{highlight(u.tax_code, query)}</strong>
                      </div>
                    )}
                    <div>ĐC: {joinAddress(a) || 'Chưa có địa chỉ'}</div>
                    {item.account_manager && (
                      <div>
                        AM: <strong>{item.account_manager.name}</strong>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
